import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Loader2, RefreshCw, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { PageContainer } from '../../../common/components/PageContainer';
import { useAnalyticsApi } from '../../../common/hooks/use-analytics-api.hook';
import { PillTabs, formatNumber } from '../../../components/design-system/admin';
import { ActivityFeed } from '../../../modules/timeline/activity/ActivityFeed';
import { ActivityKind, KIND_EVENT_TYPES, TimelineRow, buildSessions } from '../../../modules/timeline/activity/activity-feed';
import { VizHeader } from '../../../modules/vendor/analytics/viz/components/VizHeader';
import { LiveNow } from '../../../modules/vendor/analytics/viz/components/LiveNow';
import { NoTrafficState } from '../../../modules/vendor/analytics/viz/components/NoTrafficState';

type Filter = 'all' | ActivityKind;
const FILTERS: Filter[] = ['all', 'try', 'save', 'upload', 'shop', 'favorite', 'email'];
const PAGE_SIZE = 100;
const REFRESH_MS = 60_000;

/**
 * What people did in the visualizer, as a feed of visits told in sentences —
 * newest first, grouped by visitor, filterable by the kind of thing they did.
 * `?email=` narrows it to one lead.
 */
export const Timeline = () => {
  const { t } = useTranslation();
  const analytics = useAnalyticsApi();
  const [params, setParams] = useSearchParams();
  const email = params.get('email') || '';
  const [filter, setFilter] = useState<Filter>('all');
  const [intentOnly, setIntentOnly] = useState(false);
  const [rows, setRows] = useState<TimelineRow[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [failed, setFailed] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const request = useRef(0);

  const fetchPage = useCallback(
    (n: number) =>
      analytics.fetchTimelineEvents({
        page: n,
        limit: PAGE_SIZE,
        sortOrder: 'desc',
        ...(email ? { email } : {}),
        ...(filter !== 'all' ? { eventTypes: KIND_EVENT_TYPES[filter] } : {}),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [email, filter],
  );

  // First page whenever the question changes.
  useEffect(() => {
    const id = ++request.current;
    setLoading(true);
    setFailed(false);
    fetchPage(1)
      .then((res) => {
        if (id !== request.current) return;
        setRows((res?.data as any[]) ?? []);
        setPage(1);
        setHasMore(!!res?.pagination?.hasNext);
      })
      .catch(() => id === request.current && setFailed(true))
      .finally(() => id === request.current && setLoading(false));
  }, [fetchPage]);

  // Newest events merged in, keeping what was paged in below them.
  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await fetchPage(1);
      const fresh = ((res?.data as any[]) ?? []) as TimelineRow[];
      setRows((prev) => {
        const seen = new Set(fresh.map((r) => r._id));
        return [...fresh, ...prev.filter((r) => !seen.has(r._id))];
      });
      setNow(new Date());
    } catch {
      /* keep what is on screen */
    } finally {
      setRefreshing(false);
    }
  }, [fetchPage]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [refresh]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const res = await fetchPage(page + 1);
      const more = ((res?.data as any[]) ?? []) as TimelineRow[];
      setRows((prev) => {
        const seen = new Set(prev.map((r) => r._id));
        return [...prev, ...more.filter((r) => !seen.has(r._id))];
      });
      setPage((p) => p + 1);
      setHasMore(!!res?.pagination?.hasNext);
    } finally {
      setLoadingMore(false);
    }
  };

  const sessions = useMemo(() => buildSessions(rows), [rows]);
  const shown = intentOnly ? sessions.filter((s) => s.intent) : sessions;
  const visitors = useMemo(() => new Set(sessions.map((s) => s.visitorId)).size, [sessions]);
  const withIntent = sessions.filter((s) => s.intent).length;

  const clearEmail = () => {
    const next = new URLSearchParams(params);
    next.delete('email');
    setParams(next, { replace: true });
  };

  return (
    <PageContainer breadcrumbs={[{ label: t('SIDEBAR.VISUALIZER.TITLE') }, { label: t('SIDEBAR.VISUALIZER.ACTIVITY') }]}>
      <div className="mx-auto flex max-w-[960px] flex-col gap-6" data-testid="viz-activity">
        <VizHeader
          title={t('VIZ_ANALYTICS.ACTIVITY.TITLE')}
          description={t('VIZ_ANALYTICS.ACTIVITY.SUBTITLE')}
          actions={
            <>
              <LiveNow />
              <Button variant="outline" size="sm" className="h-8" onClick={refresh} disabled={refreshing || loading} aria-label={t('VIZ_ANALYTICS.COMMON.REFRESH')}>
                <RefreshCw className={cn(refreshing && 'animate-spin')} />
                <span className="hidden sm:inline">{t('VIZ_ANALYTICS.COMMON.REFRESH')}</span>
              </Button>
            </>
          }
        />

        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <PillTabs<Filter>
              value={filter}
              onChange={setFilter}
              ariaLabel={t('VIZ_ANALYTICS.ACTIVITY.FILTER')}
              options={FILTERS.map((f) => ({ value: f, label: t(`VIZ_ANALYTICS.ACTIVITY.KIND.${f.toUpperCase()}`) }))}
            />
            <Button
              variant="outline"
              size="sm"
              className={cn('h-8', intentOnly && 'border-foreground text-foreground')}
              aria-pressed={intentOnly}
              onClick={() => setIntentOnly((v) => !v)}
            >
              {t('VIZ_ANALYTICS.ACTIVITY.INTENT_ONLY')}
            </Button>
          </div>
          {email && (
            <span className="inline-flex w-fit max-w-full items-center gap-2 rounded-lg border px-2.5 py-1 text-xs">
              <span className="truncate">{t('VIZ_ANALYTICS.ACTIVITY.FOR_LEAD', { email })}</span>
              <button type="button" onClick={clearEmail} className="text-muted-foreground hover:text-foreground" aria-label={t('VIZ_ANALYTICS.COMMON.CLEAR')}>
                <X className="size-3.5" />
              </button>
            </span>
          )}
          {!loading && sessions.length > 0 && (
            <p className="text-xs tabular-nums text-muted-foreground">
              {t('VIZ_ANALYTICS.ACTIVITY.SUMMARY', {
                visits: formatNumber(sessions.length),
                visitors: formatNumber(visitors),
                intent: formatNumber(withIntent),
                count: sessions.length,
              })}
            </p>
          )}
        </div>

        {failed ? (
          <Card>
            <CardContent className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">{t('VIZ_ANALYTICS.COMMON.LOAD_FAILED')}</p>
              <Button variant="outline" size="sm" onClick={refresh}>
                {t('VIZ_ANALYTICS.COMMON.RETRY')}
              </Button>
            </CardContent>
          </Card>
        ) : !loading && rows.length === 0 && filter === 'all' && !email ? (
          <NoTrafficState title={t('VIZ_ANALYTICS.ACTIVITY.EMPTY_TITLE')} />
        ) : (
          <Card className="py-0">
            <CardContent className="px-4 sm:px-6">
              <ActivityFeed
                sessions={shown}
                loading={loading}
                now={now}
                emptyText={intentOnly ? t('VIZ_ANALYTICS.ACTIVITY.EMPTY_INTENT') : t('VIZ_ANALYTICS.ACTIVITY.EMPTY_FILTER')}
              />
            </CardContent>
          </Card>
        )}

        {!loading && hasMore && (
          <Button variant="outline" className="self-center" onClick={loadMore} disabled={loadingMore}>
            {loadingMore && <Loader2 className="animate-spin" />}
            {t('VIZ_ANALYTICS.ACTIVITY.LOAD_MORE')}
          </Button>
        )}
      </div>
    </PageContainer>
  );
};

export default Timeline;
